import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { VIDEOS, POSTERS } from "@/lib/business";
import { useVoiceOver } from "@/lib/useVoiceOver";

const ITEMS = [
  { key: "bodywork", video: VIDEOS.bodywork, poster: POSTERS.bodywork, span: "md:col-span-7 md:row-span-2" },
  { key: "painting", video: VIDEOS.painting, poster: POSTERS.painting, span: "md:col-span-5" },
  { key: "accident", video: VIDEOS.accident, poster: POSTERS.accident, span: "md:col-span-5" },
];

function VideoCard({ item, index, label, narration, playLabel, stopLabel, voActive, onActivateVoice, onDeactivateVoice }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const hasVideo = !!item.video;

  const toggle = () => {
    // If no real video, just trigger voiceover narration
    if (!hasVideo) {
      if (playing) {
        setPlaying(false);
        onDeactivateVoice();
      } else {
        setPlaying(true);
        onActivateVoice();
      }
      return;
    }
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
      onActivateVoice();
    } else {
      v.pause();
      setPlaying(false);
      onDeactivateVoice();
    }
  };

  useEffect(() => {
    if (!hasVideo) return;
    const v = ref.current;
    if (!v) return;
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    v.addEventListener("pause", onPause);
    v.addEventListener("play", onPlay);
    return () => {
      v.removeEventListener("pause", onPause);
      v.removeEventListener("play", onPlay);
    };
  }, [hasVideo]);

  return (
    <article
      data-testid={`video-card-${index}`}
      className={`relative group overflow-hidden rounded-xl border border-white/10 bg-[#0e0e0e] hover:border-white/30 transition-all duration-500 min-h-[280px] md:min-h-[380px] ${item.span}`}
    >
      {hasVideo ? (
        <video
          ref={ref}
          data-testid={`video-card-video-${index}`}
          poster={item.poster}
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity duration-500"
        >
          <source src={item.video} type="video/mp4" />
        </video>
      ) : (
        <div
          data-testid={`video-card-poster-${index}`}
          className={`absolute inset-0 w-full h-full bg-cover bg-center opacity-85 group-hover:opacity-100 transition-all duration-700 ${
            playing ? "animate-kenburns" : "group-hover:scale-105"
          }`}
          style={{ backgroundImage: `url(${item.poster})` }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

      <button
        data-testid={`video-card-play-${index}`}
        onClick={toggle}
        aria-label={playing ? stopLabel : playLabel}
        className="absolute inset-0 flex items-center justify-center"
      >
        <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 backdrop-blur-xl border border-white/30 flex items-center justify-center group-hover:bg-[#e52620] group-hover:border-[#e52620] transition-all duration-500 active:scale-95">
          {playing ? (
            <Pause className="w-7 h-7 text-white" fill="currentColor" />
          ) : (
            <Play className="w-7 h-7 text-white ml-1" fill="currentColor" />
          )}
        </span>
      </button>

      {/* Voiceover indicator */}
      {playing && (
        <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-md bg-black/70 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.2em] font-bold font-mono text-white">
          {voActive ? (
            <>
              <Volume2 className="w-3 h-3 animate-pulse text-[#e52620]" />
              Voiceover
            </>
          ) : (
            <>
              <VolumeX className="w-3 h-3" />
              Stumm
            </>
          )}
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-mono font-bold mb-1">
          0{index + 1} · {label.tag}
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight">
          {label.title}
        </h3>
        {playing && (
          <p className="mt-3 text-sm text-zinc-300 leading-relaxed max-w-md pointer-events-none">
            {narration}
          </p>
        )}
      </div>
    </article>
  );
}

export default function VideoGallery() {
  const { t, lang } = useI18n();
  const { speak, stop, speaking } = useVoiceOver();
  const [activeIndex, setActiveIndex] = useState(-1);

  return (
    <section
      id="gallery"
      data-testid="video-gallery-section"
      className="relative py-16 md:py-20 bg-[#080808] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#e52620]" />
              <span
                data-testid="gallery-eyebrow"
                className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold font-mono"
              >
                {t.gallery.eyebrow}
              </span>
            </div>
            <h2
              data-testid="gallery-title"
              className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95] text-balance"
            >
              {t.gallery.title}
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed">{t.gallery.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:grid-rows-2 gap-5">
          {ITEMS.map((item, i) => (
            <VideoCard
              key={item.key}
              item={item}
              index={i}
              label={t.gallery.videos[i]}
              narration={t.gallery.videos[i].narration}
              playLabel={t.gallery.play}
              stopLabel={t.gallery.stop}
              voActive={speaking && activeIndex === i}
              onActivateVoice={() => {
                setActiveIndex(i);
                speak(t.gallery.videos[i].narration, lang);
              }}
              onDeactivateVoice={() => {
                if (activeIndex === i) {
                  stop();
                  setActiveIndex(-1);
                }
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
